import React from "react";
import styled from "styled-components";
import { useAppStateContext } from "hooks/useAppState/useAppState";
import useDevices from "hooks/livekit/useDevices/useDevices";
import useMediaStreamTrack from "hooks/livekit/useMediaStreamTrack/useMediaStreamTrack";
import useSelectedAudioInput from "hooks/livekit/useSelectedAudioInput/useSelectedAudioInput";
import useVideoContext from "hooks/livekit/useVideoContext/useVideoContext";
import { Modal } from "components/shared/Modal/Modal";
import { DeviceDropdown } from "../Join/DeviceDropdown/DeviceDropdown";
import { MicMeter } from "../Join/MicMeter/MicMeter";
import { TestSpeakers } from "../Join/TestSpeakers/TestSpeakers";
import { MicIcon, VolumeIcon } from "../Join/Icons";
import { CloseIcon } from "../shared/ActionBar/icons/CloseIcon";
import { colors } from "utils";

export default function DeviceSettingsModal() {
  const {
    modal: { isDeviceSettingsModalOpen, closeDeviceSettingsModal },
  } = useAppStateContext();
  const { audioInputDevices, audioOutputDevices } = useDevices();
  const { localTracks, activeAudioSinkId, setActiveAudioSinkId } = useVideoContext();
  const localAudioTrack = localTracks.find((track) => track.kind === "audio");
  const mediaStreamTrack = useMediaStreamTrack(localAudioTrack);
  const trackDeviceId = mediaStreamTrack?.getSettings().deviceId;
  const [localAudioInputDeviceId, selectInputDevice, clearPendingInputDevice] =
    useSelectedAudioInput(trackDeviceId);

  const replaceAudioInputTrack = (newDeviceId) => {
    selectInputDevice(newDeviceId);
    if (localAudioTrack) {
      localAudioTrack.restart({ deviceId: { exact: newDeviceId } }).catch(clearPendingInputDevice);
    }
  };

  return (
    <Modal open={isDeviceSettingsModalOpen} onClose={closeDeviceSettingsModal}>
      <ModalContent>
        <Header>
          <Title>Audio Settings</Title>
          <CloseButton
            type="button"
            aria-label="Close audio settings"
            onClick={closeDeviceSettingsModal}
          >
            <CloseIcon />
          </CloseButton>
        </Header>
        <Content>
          <Column>
            <SectionLabel>Microphone</SectionLabel>
            <DeviceDropdown
              icon={<MicIcon />}
              devices={audioInputDevices}
              selectedDeviceId={localAudioInputDeviceId}
              onSelect={replaceAudioInputTrack}
              fallbackLabel="No microphone found"
              dropDown
            />
            <MicMeter mediaStreamTrack={mediaStreamTrack} />
            {/* TODO: Uncomment when troubleshooting flow is implemented
            <TroubleshootLink
              href="https://support.google.com/chrome/answer/2693767"
              target="_blank"
              rel="noopener noreferrer"
            >
              Is the microphone not working?
            </TroubleshootLink>
            */}
          </Column>
          <Column>
            <SectionLabel>Speaker</SectionLabel>
            <DeviceDropdown
              icon={<VolumeIcon />}
              devices={audioOutputDevices}
              selectedDeviceId={activeAudioSinkId}
              onSelect={setActiveAudioSinkId}
              fallbackLabel="System Default"
              dropDown
            />
            <TestSpeakers activeAudioSinkId={activeAudioSinkId} />
            {/* TODO: Uncomment when troubleshooting flow is implemented
            <TroubleshootLink
              href="https://support.google.com/chrome/answer/2693767"
              target="_blank"
              rel="noopener noreferrer"
            >
              Is the speaker not working?
            </TroubleshootLink>
            */}
          </Column>
        </Content>
      </ModalContent>
    </Modal>
  );
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
  min-width: 520px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h1`
  font-family: 'ABC Marfa', sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const CloseButton = styled.button`
  width: 48px;
  height: 48px;
  border: 2px solid ${colors.nightlyWoods950};
  border-radius: 100px;
  background: transparent;
  color: ${colors.nightlyWoods950};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    background-color: ${colors.milkGlass600};
  }
`;

const Content = styled.div`
  display: flex;
  gap: 24px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const SectionLabel = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
`;

const TroubleshootLink = styled.a`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.blue500};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${colors.blue800};
  }
`;
